/**
 * The Alpha OPS vault, closed: a round steel vault door in a black marble bay with
 * red neon, seen low and wide through a fisheye.
 *
 * Asset contract (public/3d/alpha-ops/, produced by the Blender pipeline in alpha-ops/,
 * outside this repo: scripts/scene/ext_preview.py --export, then scripts/web/build_web.py):
 *   vault.glb      geometry, placeholder materials matched by name below. Stone, floor and
 *                  lettering carry TEXCOORD_0 = world metres (tiling detail) and
 *                  TEXCOORD_1 = the lightmap atlas. The turned steel carries TEXCOORD_0 = a
 *                  front-on projection centred on the door axis. Moving parts keep their
 *                  names and pivots (OpsDoor on the hinge axis; OpsHub, OpsBar*, OpsBolts on
 *                  the door axis) for the unlock sequence.
 *   lightmap.webp  baked Cycles diffuse light, (E / lightmapScale) ^ (1 / 2.2)
 *   puddles.webp   floor wet mask on the lightmap atlas
 *   env.exr        what the steel mirrors: a panorama from in front of the door with the
 *                  reflection cards the camera never sees
 *   door_ao.webp   contact shadows of the steel parts (door shut) on their TEXCOORD_1 atlas
 *   tex/*          tiling marble and concrete, and the door's brushing-direction map
 *
 * The turned rings on the door face are drawn in the shader (anti-aliased), not a texture:
 * they stay crisp at any resolution and cost nothing to download.
 *
 * Unlock (unlock(), after the page's hold-to-unlock fills; setCharge mirrors the hold so the door
 * reacts): the Alpha mark flashes, the hub turns, the eight bars draw in, the edge bolts
 * retract, the door eases out and swings open on its hinge, and the camera moves in on the opening.
 * onOpened fires once all of that has (nearly) settled.
 *
 * Behind the door: the treasure room (treasure.ts), fetched in the background as soon as the vault
 * is up, shown when the door starts to swing; its light comes up as the door opens.
 *
 * Lighting split: everything static is lit only by its lightmap (no real-time lights, no
 * environment); the steel is lit only by the environment; the neon is unlit HDR colour
 * that the bloom picks up. The wet floor adds a live planar mirror in its puddles.
 */
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { EXRLoader } from 'three/examples/jsm/loaders/EXRLoader.js';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js';
import { Reflector } from 'three/examples/jsm/objects/Reflector.js';
import { createPost } from './post';
import { loadTreasure } from './treasure';

// not public/alpha-ops/: a folder there would shadow the /alpha-ops route on the dev server
const BASE = '/3d/alpha-ops/';

/** Blender (Z-up, metres) -> three.js (Y-up). */
const b2t = (x, y, z) => new THREE.Vector3(x, z, -y);

const FLOOR_LAYER = 1; // the floor samples the mirror, so the mirror pass must not draw it
const INTRO_MS = 5200; // camera settle
const STRIKE_AT = 350; // the neon strikes this long after start
const STRIKE_MS = 1500;
const smooth = (a, b, t) => {
  const x = THREE.MathUtils.clamp((t - a) / (b - a), 0, 1);
  return x * x * (3 - 2 * x);
};
const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
/** eased 0..1 progress of a step that runs from a to b ms into the unlock */
const span = (ms, a, b, ease = easeInOutCubic) => ease(THREE.MathUtils.clamp((ms - a) / (b - a), 0, 1));

// unlock timeline (ms): a heavy, mechanical order of events
const U = {
  flash: [0, 450],
  hub: [150, 1500], // the hub turns 270 degrees
  bars: [350, 1250], // the bars draw in toward it
  bolts: [800, 1450], // the edge bolts let go of the frame
  clunk: 1450, // ... felt as a small jolt
  pull: [1450, 1850], // the door eases out of the frame
  swing: [1750, 4600], // and swings open on its hinge
  opened: 5300, // the form comes up once the door is fully open and the camera has nearly settled
};
const BAR_TRAVEL = 0.12; // metres the bars slide in
const BOLT_DRAW = 0.82; // the bolt ring shrinks to this radius (inside the door edge)
const DOOR_PULL = 0.1; // metres out of the frame before it swings
const DOOR_SWING = THREE.MathUtils.degToRad(-100); // toward the camera
// once it swings, the camera moves in until the dark opening fills most of the frame: it stops this far
// in front of the door plane, level with the door axis (metres)
const INSIDE_DIST = 2.1;
const INSIDE_RISE = 0.2; // camera a touch below the axis, looking slightly up into it

/** Neon tubes striking: a few stutters, then full. u = 0..1 over STRIKE_MS. */
function strike(u) {
  if (u <= 0) return 0;
  if (u >= 1) return 1;
  const keys = [
    [0, 0],
    [0.06, 0.9],
    [0.1, 0.1],
    [0.18, 1],
    [0.24, 0.2],
    [0.33, 0.25],
    [0.4, 1],
    [0.47, 0.6],
    [0.55, 1],
    [1, 1],
  ];
  for (let i = 1; i < keys.length; i++) {
    if (u <= keys[i][0]) {
      const [t0, v0] = keys[i - 1];
      const [t1, v1] = keys[i];
      return v0 + ((u - t0) / (t1 - t0)) * (v1 - v0);
    }
  }
  return 1;
}

/** Stone: the marble's veins faded toward plain dark stone, optional slab joints (metres). */
function stonePatch(veins, base, dim, tile, seams) {
  return (shader) => {
    shader.uniforms.veins = { value: veins };
    shader.uniforms.stoneBase = { value: new THREE.Color(base, base, base * 1.04) };
    shader.uniforms.dim = { value: dim };
    shader.uniforms.tile = { value: tile };
    shader.uniforms.seams = { value: new THREE.Vector2(...(seams ?? [0, 0])) };
    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <common>',
        '#include <common>\nuniform float veins;\nuniform vec3 stoneBase;\nuniform float dim;\nuniform float tile;\nuniform vec2 seams;',
      )
      .replace(
        '#include <map_fragment>',
        `vec4 sampledDiffuseColor = texture2D( map, vMapUv );
	vec3 stone = mix( stoneBase, sampledDiffuseColor.rgb, veins ) * dim;
	if ( seams.x > 0.0 ) {
		vec2 m = vMapUv * tile;                                  // back to metres on the face
		vec2 d = abs( fract( m / seams + 0.5 ) - 0.5 ) * seams;  // distance to the nearest joint
		vec2 w = fwidth( m ) * 0.75 + 0.002;
		float joint = 1.0 - min( smoothstep( 0.0, w.x, d.x ), smoothstep( 0.0, w.y, d.y ) );
		stone *= 1.0 - 0.85 * joint;
	}
	diffuseColor.rgb *= stone;`,
      );
  };
}

/**
 * Lathe-turned steel on the planar door UVs (metres from the door axis = (uv - 0.5) * 2R):
 * irregular bands ~3 cm apart plus a finer ripple, warped by noise, as in the Cycles material.
 * Each ring set fades to its average once it gets finer than about two pixels, so it never
 * shimmers. tint: colour swing (0 = none), rough: roughness swing.
 */
function turnedRings(uvRadius, tint, rough) {
  return (shader) => {
    shader.uniforms.doorUvR = { value: uvRadius };
    shader.uniforms.ringTint = { value: tint };
    shader.uniforms.ringRough = { value: rough };
    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <common>',
        `#include <common>
uniform float doorUvR;
uniform float ringTint;
uniform float ringRough;
float opsHash( vec2 p ) { return fract( sin( dot( p, vec2( 127.1, 311.7 ) ) ) * 43758.5453 ); }
float opsNoise( vec2 p ) {
	vec2 i = floor( p ), f = fract( p ), u = f * f * ( 3.0 - 2.0 * f );
	return mix( mix( opsHash( i ), opsHash( i + vec2( 1.0, 0.0 ) ), u.x ),
	            mix( opsHash( i + vec2( 0.0, 1.0 ) ), opsHash( i + vec2( 1.0, 1.0 ) ), u.x ), u.y );
}
float opsFbm( vec2 p ) {
	float a = 0.5, s = 0.0;
	for ( int k = 0; k < 4; k ++ ) { s += a * opsNoise( p ); p *= 2.03; a *= 0.5; }
	return s;
}
float opsRings( float x ) {
	float v = 0.5 + 0.5 * sin( 6.2831853 * x );
	return mix( v, 0.5, smoothstep( 0.25, 0.6, fwidth( x ) ) );
}`,
      )
      .replace(
        '#include <roughnessmap_fragment>',
        `#include <roughnessmap_fragment>
	vec2 dp = ( vAnisotropyMapUv - 0.5 ) * 2.0 * doorUvR;
	float rr = length( dp );
	float ring = 0.7 * opsRings( rr / 0.028 + 0.64 * ( opsFbm( dp * 2.5 ) - 0.5 ) )
	           + 0.3 * opsRings( rr / 0.0095 + 0.25 * ( opsFbm( dp * 6.0 ) - 0.5 ) );
	diffuseColor.rgb *= 1.0 + ringTint * ( ring - 0.5 );
	roughnessFactor *= 1.0 + ringRough * ( ring - 0.5 );`,
      );
  };
}

export function createVault(container, cb, opts = {}) {
  const mobile = window.matchMedia('(max-width: 47.9375rem), (pointer: coarse)').matches;
  // dev aid: ?off=bloom,grade,mirror,fisheye switches pieces off for tuning
  const off = new Set(new URLSearchParams(window.location.search).get('off')?.split(',') ?? []);

  const renderer = new THREE.WebGLRenderer({ antialias: false, powerPreference: 'high-performance' });
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.AgXToneMapping; // the Blender look-dev used AgX too
  container.appendChild(renderer.domElement);

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x000000);
  const camera = new THREE.PerspectiveCamera(60, 1, 0.05, 80);
  camera.rotation.order = 'YXZ';
  camera.layers.enable(FLOOR_LAYER);
  const post = createPost(renderer, scene, camera, { bloom: !off.has('bloom'), grade: !off.has('grade') });

  const manager = new THREE.LoadingManager();
  manager.onProgress = (_url, loaded, total) => cb.onProgress(total ? loaded / total : 0);
  const gltf = new GLTFLoader(manager).setMeshoptDecoder(MeshoptDecoder);
  const texLoader = new THREE.TextureLoader(manager);
  const exrLoader = new EXRLoader(manager).setDataType(THREE.HalfFloatType);

  const disposables = [post];
  let reflector = null;
  let manifest = null;
  let raf = 0;
  let disposed = false;
  let ready = false;
  let startAt = -1;
  let introDone = false;

  // what the intro drives, filled in by load()
  const baked = [];
  const steel = [];
  const neon = [];
  const part = (obj) =>
    obj ? { obj, pos: obj.position.clone(), rot: obj.rotation.clone(), scale: obj.scale.clone() } : null;
  let door = null;
  let hub = null;
  let bolts = null;
  const bars = [];
  const anchorPoint = new THREE.Vector3();
  let hasAnchor = false;
  let charge = 0;
  let chargeTarget = 0;
  let unlockAt = -1;
  let opened = false;
  let treasure = null;
  let treasureRequested = false;
  let floorMirror = null;

  // ------------------------------------------------------------ framing
  // Rest pose from the Blender camera; phones step back and lengthen the lens so the bay fits.
  const rest = { pos: new THREE.Vector3(), pitch: 0 };
  const lens = { focal: 1.11, strength: 1, srcTan: 1.6 };
  let overscan = 1.5;
  let quality = 1;
  const pointer = new THREE.Vector2();
  const pointerSmooth = new THREE.Vector2();

  const frame = (aspect) => {
    if (!manifest) return;
    const [bx, by, bz] = manifest.camera.position;
    const f = manifest.camera.fisheyeLensMm / (manifest.camera.sensorMm / 2);
    // landscape: keep the door the size it is in Blender's 16:9 frame (fit the height), but never
    // narrower than what still shows the ALPHA OPS lettering on the left slab (~4.2 m out)
    const fitHeight = (f * 16) / 9 / aspect;
    const wide = Math.min(fitHeight, 1.45) / f;
    // portrait: back the camera off and lengthen the lens so the bay (+-2.5 m) fills the width
    const tall = smooth(1.0, 0.5, aspect);
    const dist = THREE.MathUtils.lerp(-by, 7.2, tall);
    const fitBay = 1 / (2 * Math.sin(Math.atan(2.5 / dist) / 2));
    lens.focal = THREE.MathUtils.lerp(f * wide, fitBay, tall);
    lens.strength = off.has('fisheye') ? 0 : 1;
    rest.pos.copy(b2t(bx, -dist, THREE.MathUtils.lerp(bz, 1.35, tall)));
    rest.pitch = THREE.MathUtils.degToRad(THREE.MathUtils.lerp(manifest.camera.pitchDeg, 4, tall));

    // the perspective source must reach the fisheye's corners
    const rc = Math.hypot(1, 1 / aspect);
    const thFish = 2 * Math.asin(Math.min(0.999, rc / (2 * lens.focal)));
    const theta = THREE.MathUtils.lerp(Math.atan(rc / lens.focal), thFish, lens.strength);
    lens.srcTan = (Math.tan(theta) / rc) * 1.02;
    camera.aspect = aspect;
    camera.fov = THREE.MathUtils.radToDeg(2 * Math.atan(lens.srcTan / aspect));
    camera.updateProjectionMatrix();
    // centre sharpness: the source needs focal * srcTan times the screen's pixels across
    overscan = Math.min(lens.focal * lens.srcTan, mobile ? 1.35 : 1.7);
  };

  const MAX_SRC_PIXELS = mobile ? 3.2e6 : 10e6;
  let dpr = window.devicePixelRatio;
  const resize = () => {
    dpr = window.devicePixelRatio;
    const w = container.clientWidth;
    const h = container.clientHeight;
    frame(w / Math.max(1, h));
    const pr = Math.max(
      0.5,
      Math.min(window.devicePixelRatio, mobile ? 1.5 : 2, Math.sqrt(MAX_SRC_PIXELS / Math.max(1, w * h)) / overscan) *
        quality,
    );
    renderer.setPixelRatio(pr);
    renderer.setSize(w, h);
    const cw = Math.round(w * pr);
    const ch = Math.round(h * pr);
    const sw = Math.round(cw * overscan);
    const sh = Math.round(ch * overscan);
    post.setSize(cw, ch, sw, sh, lens);
    reflector?.getRenderTarget().setSize(Math.round(sw * 0.5), Math.round(sh * 0.5));
  };

  const onPointer = (e) => {
    pointer.set((e.clientX / window.innerWidth) * 2 - 1, (e.clientY / window.innerHeight) * 2 - 1);
  };
  window.addEventListener('pointermove', onPointer);

  const loadTex = (url, srgb, repeat = 0) =>
    new Promise((resolve, reject) => {
      texLoader.load(
        BASE + url,
        (t) => {
          t.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
          t.flipY = false; // glTF UV convention
          t.anisotropy = Math.min(8, renderer.capabilities.getMaxAnisotropy());
          if (repeat) {
            t.wrapS = t.wrapT = THREE.RepeatWrapping;
            t.repeat.set(1 / repeat, 1 / repeat);
          }
          disposables.push(t);
          resolve(t);
        },
        undefined,
        reject,
      );
    });

  async function load() {
    manifest = await (await fetch(BASE + 'manifest.json')).json();
    const M = manifest;
    const T = M.textures;
    renderer.toneMappingExposure = Math.pow(2, M.exposure);

    const [room, lightmap, puddles, marbleColor, floorColor, floorNormal, floorArm, steelAniso, doorAo, env] =
      await Promise.all([
        gltf.loadAsync(BASE + 'vault.glb'),
        loadTex(T.lightmap, true),
        loadTex(T.puddles, false),
        loadTex(T.marble_color, true, M.tiles.marble),
        loadTex(T.floor_color, true, M.tiles.floor),
        loadTex(T.floor_normal, false, M.tiles.floor),
        loadTex(T.floor_arm, false, M.tiles.floor),
        loadTex(T.steel_aniso, false),
        loadTex(T.door_ao, false),
        exrLoader.loadAsync(BASE + T.env),
      ]);
    if (disposed) return;
    lightmap.channel = 1;
    puddles.channel = 1;
    doorAo.channel = 1;

    // the steel's environment; the panorama was shot facing the door (-Z here), three's
    // equirect puts +X at its centre: turn it a quarter
    env.mapping = THREE.EquirectangularReflectionMapping;
    const pmrem = new THREE.PMREMGenerator(renderer);
    const envRT = pmrem.fromEquirectangular(env);
    pmrem.dispose();
    env.dispose();
    disposables.push(envRT);
    const envRot = new THREE.Euler(0, Math.PI / 2, 0);

    const lmFull = M.lightmapScale * Math.PI;
    const red = new THREE.Color(...M.red);

    const lambert = (name, color, extra = {}) => {
      const m = new THREE.MeshLambertMaterial({ name, color, lightMap: lightmap, lightMapIntensity: 0, ...extra });
      baked.push({ mat: m, full: lmFull });
      disposables.push(m);
      return m;
    };
    const metal = (name, color, roughness, extra = {}) => {
      const m = new THREE.MeshPhysicalMaterial({
        name,
        color,
        roughness,
        metalness: 1,
        envMap: envRT.texture,
        envMapIntensity: 0,
        aoMap: doorAo,
        ...extra,
      });
      m.envMapRotation.copy(envRot);
      steel.push({ mat: m, full: 1 });
      disposables.push(m);
      return m;
    };

    // stone (Cycles: marble(veins, base, dim)), seams on the main slabs only
    const marbleMats = {};
    for (const [name, veins, base, dim, seams] of [
      ['Ops_MarbleWall', 0.34, 0.04, 1.0, [1.6, 2.4]],
      ['Ops_MarbleBay', 0.35, 0.035, 0.9, null],
      ['Ops_MarblePillar', 0.32, 0.04, 1.0, null],
    ]) {
      const m = lambert(name, 0xffffff, { map: marbleColor });
      m.onBeforeCompile = stonePatch(veins, base, dim, M.tiles.marble, seams ? [seams[0], seams[1]] : null);
      marbleMats[name] = m;
    }
    const paint = lambert('Ops_LetterPaint', new THREE.Color(0.62, 0.62, 0.64));
    const paintRed = lambert('Ops_LetterPaintRed', new THREE.Color(0.75, 0.012, 0.02));
    const darkBaked = lambert('Ops_DarkSteel_baked', new THREE.Color(0.05, 0.05, 0.055));

    // the turned door steel: rings in colour and roughness (Cycles: tint 0.5..1.5, roughness x0.4..1.6),
    // brushed around the door axis
    const brushed = metal('Ops_Brushed', new THREE.Color(0.2, 0.202, 0.21), 0.19, {
      anisotropy: 0.75,
      anisotropyMap: steelAniso,
    });
    brushed.onBeforeCompile = turnedRings(M.doorUvRadius, 0.9, 1.2);
    const brushedPlain = metal('Ops_BrushedPlain', new THREE.Color(0.2, 0.202, 0.21), 0.19);
    const polished = metal('Ops_Polished', new THREE.Color(0.42, 0.425, 0.44), 0.08);
    const darkTurned = metal('Ops_DarkSteel', new THREE.Color(0.045, 0.045, 0.05), 0.28, {
      anisotropy: 0.6,
      anisotropyMap: steelAniso,
    });
    darkTurned.onBeforeCompile = turnedRings(M.doorUvRadius, 0, 0.5);
    // the edge bolts live inside the frame while the door is shut: no baked shadow on them
    const boltSteel = metal('Ops_PolishedBolts', new THREE.Color(0.42, 0.425, 0.44), 0.08, { aoMap: null });
    const darkPlain = metal('Ops_DarkSteelPlain', new THREE.Color(0.045, 0.045, 0.05), 0.28);

    const neonMat = (strength, isMark = false) => {
      const m = new THREE.MeshBasicMaterial({ color: 0x000000 });
      neon.push({ mat: m, on: red.clone().multiplyScalar(strength), mark: isMark });
      disposables.push(m);
      return m;
    };
    const tube = neonMat(M.neonStrength);
    const mark = neonMat(M.markStrength, true);

    // the wet floor: grimy concrete, glassy puddles that show a live mirror
    const floor = new THREE.MeshStandardMaterial({
      name: 'Ops_WetFloor',
      map: floorColor,
      normalMap: floorNormal,
      roughnessMap: floorArm,
      color: new THREE.Color(0.3, 0.3, 0.31),
      roughness: 1,
      metalness: 0,
      lightMap: lightmap,
      lightMapIntensity: 0,
    });
    baked.push({ mat: floor, full: lmFull });
    disposables.push(floor);

    room.scene.updateMatrixWorld(true);
    let floorBox = null;
    room.scene.traverse((obj) => {
      const mesh = obj;
      if (!mesh.isMesh) return;
      const name = mesh.material.name;
      const staticPart = /^Ops(PillarBase|StripChannel)/.test(mesh.name);
      let mat;
      if (name in marbleMats) mat = marbleMats[name];
      else if (name === 'Ops_LetterPaint') mat = paint;
      else if (name === 'Ops_LetterPaintRed') mat = paintRed;
      else if (name === 'Ops_WetFloor') {
        mat = floor;
        mesh.layers.set(FLOOR_LAYER);
        floorBox = new THREE.Box3().setFromObject(mesh);
      } else if (name === 'Ops_Brushed') mat = brushed;
      else if (name === 'Ops_BrushedPlain') mat = brushedPlain;
      else if (name === 'Ops_Polished') mat = mesh.name.startsWith('OpsBolts') ? boltSteel : polished;
      else if (name === 'Ops_DarkSteel') mat = staticPart ? darkBaked : darkTurned;
      else if (name === 'Ops_DarkSteelPlain') mat = darkPlain;
      else if (name === 'Ops_Neon') mat = tube;
      else if (name === 'Ops_RedMark') mat = mark;
      if (mat) mesh.material = mat;
      else console.warn('[alpha-ops] no material for', name, mesh.name);
    });
    scene.add(room.scene);

    // moving parts (Blender names; pivots: OpsDoor on the hinge axis, the rest on the door axis)
    door = part(room.scene.getObjectByName('OpsDoor'));
    hub = part(room.scene.getObjectByName('OpsHub'));
    bolts = part(room.scene.getObjectByName('OpsBolts'));
    for (let i = 0; i < 8; i++) {
      const p = part(room.scene.getObjectByName(`OpsBar${i}`));
      const a = (2 * Math.PI * i) / 8; // Blender (cos a, 0, sin a) around the axis -> three (cos a, sin a, 0)
      if (p) bars.push({ p, dir: new THREE.Vector3(Math.cos(a), Math.sin(a), 0) });
    }
    // the hold-to-unlock sits on the right wall, mirroring the painted ALPHA OPS on the left
    const word = new THREE.Box3();
    for (const n of ['OpsWordAlpha', 'OpsWordOps']) {
      const o = room.scene.getObjectByName(n);
      if (o) word.expandByObject(o);
    }
    if (!word.isEmpty()) {
      word.getCenter(anchorPoint);
      anchorPoint.x = -anchorPoint.x;
      hasAnchor = true;
    }
    disposables.push({ dispose: () => room.scene.traverse((o) => o.geometry?.dispose()) });

    // planar mirror over the floor; its own draw is masked, it only renders the target the floor samples
    if (floorBox && !off.has('mirror')) {
      const box = floorBox;
      const size = box.getSize(new THREE.Vector3());
      const center = box.getCenter(new THREE.Vector3());
      reflector = new Reflector(new THREE.PlaneGeometry(size.x, size.z), {
        textureWidth: 512,
        textureHeight: 512,
        clipBias: 0.003,
        multisample: 0,
      });
      reflector.rotation.x = -Math.PI / 2;
      reflector.position.set(center.x, box.max.y + 0.0005, center.z);
      const rm = reflector.material;
      rm.colorWrite = false;
      rm.depthWrite = false;
      reflector.renderOrder = -1; // update the reflection before the floor draws
      scene.add(reflector);
      reflector.updateMatrixWorld(true);
      reflector.getReflectionCamera(camera).layers.disable(FLOOR_LAYER);
      disposables.push(reflector);
      const refl = {
        reflMap: { value: reflector.getRenderTarget().texture },
        reflTexMatrix: reflector.material.uniforms.textureMatrix,
        reflInvWorld: { value: reflector.matrixWorld.clone().invert() },
        puddleMap: { value: puddles },
        mirrorLevel: { value: 0 },
      };
      floorMirror = refl.mirrorLevel;
      floor.onBeforeCompile = (shader) => {
        Object.assign(shader.uniforms, refl);
        shader.vertexShader = shader.vertexShader
          .replace(
            '#include <common>',
            '#include <common>\nuniform mat4 reflTexMatrix;\nuniform mat4 reflInvWorld;\nvarying vec4 vReflCoord;',
          )
          .replace(
            '#include <project_vertex>',
            '#include <project_vertex>\n\tvReflCoord = reflTexMatrix * reflInvWorld * modelMatrix * vec4( transformed, 1.0 );',
          );
        shader.fragmentShader = shader.fragmentShader
          .replace(
            '#include <common>',
            '#include <common>\nuniform sampler2D reflMap;\nuniform sampler2D puddleMap;\nuniform float mirrorLevel;\nvarying vec4 vReflCoord;',
          )
          .replace(
            '#include <map_fragment>',
            `#include <map_fragment>
	float wet = smoothstep( 0.1, 0.6, texture2D( puddleMap, vLightMapUv ).r );
	diffuseColor.rgb *= mix( 1.0, 0.6, wet );                 // wet concrete reads darker`,
          )
          .replace(
            '#include <normal_fragment_maps>',
            `#include <normal_fragment_maps>
	vec3 bumpN = normal;
	normal = normalize( mix( normal, nonPerturbedNormal, wet ) );  // water fills the texture`,
          )
          .replace(
            '#include <lights_fragment_end>',
            `#include <lights_fragment_end>
	// the mirror: crisp in the puddles, a dimmer smear on the damp concrete (Fresnel, grazing view)
	vec4 rc = vReflCoord;
	rc.xy += ( bumpN.xy - nonPerturbedNormal.xy ) * ( 1.0 - wet ) * 0.08 * rc.w;
	vec3 mirrorCol = texture2DProj( reflMap, rc ).rgb;
	float fres = 0.04 + 0.96 * pow( 1.0 - saturate( dot( nonPerturbedNormal, normalize( vViewPosition ) ) ), 5.0 );
	reflectedLight.indirectSpecular += mirrorCol * fres * mix( 0.06, 0.75, wet ) * mirrorLevel;`,
          );
      };
    }

    window.addEventListener('resize', resize);
    resize();
    applyPower(0, 0);
    camera.position.copy(rest.pos);
    camera.rotation.set(rest.pitch, 0, 0);
    // compile shaders and upload textures before the loader lifts, so the intro never stutters
    await renderer.compileAsync(scene, camera);
    if (disposed) return;
    ready = true;
    cb.onReady();
  }

  /** room light (lightmaps, steel reflections, mirror) and the neon, both 0..1 */
  function applyPower(room, tubes, markBoost = 1) {
    for (const b of baked) b.mat.lightMapIntensity = b.full * room;
    for (const s of steel) s.mat.envMapIntensity = s.full * (0.15 + 0.85 * room);
    if (floorMirror) floorMirror.value = room;
    for (const n of neon) n.mat.color.copy(n.on).multiplyScalar(tubes * (n.mark ? markBoost : 1));
  }

  /** the room behind the door: fetched quietly once the vault is up, with its own loaders (so it never
   *  touches the loading screen's progress), compiled before it can be needed */
  function prefetchTreasure() {
    if (treasureRequested || disposed) return;
    treasureRequested = true;
    loadTreasure(
      renderer,
      new GLTFLoader().setMeshoptDecoder(MeshoptDecoder),
      new THREE.TextureLoader(),
      new EXRLoader().setDataType(THREE.HalfFloatType),
      { reducedMotion: opts.reducedMotion },
    )
      .then(async (t) => {
        if (disposed) {
          t.dispose();
          return;
        }
        t.setLevel(0);
        scene.add(t.group);
        await renderer.compileAsync(scene, camera);
        if (disposed) return;
        t.group.visible = unlockAt >= 0;
        treasure = t;
        disposables.push(t);
      })
      .catch((err) => console.warn("[alpha-ops] the room behind the door didn't load", err));
  }

  /** the unlock, ms after it started */
  function animateUnlock(ms) {
    if (hub) hub.obj.rotation.z = hub.rot.z - span(ms, ...U.hub) * Math.PI * 1.5; // clockwise as seen
    const draw = span(ms, ...U.bars);
    for (const b of bars) b.p.obj.position.copy(b.p.pos).addScaledVector(b.dir, -BAR_TRAVEL * draw);
    if (bolts) {
      const k = THREE.MathUtils.lerp(1, BOLT_DRAW, span(ms, ...U.bolts));
      bolts.obj.scale.set(bolts.scale.x * k, bolts.scale.y * k, bolts.scale.z);
    }
    if (door) {
      door.obj.position.copy(door.pos);
      door.obj.position.z += DOOR_PULL * span(ms, ...U.pull, easeOutCubic);
      door.obj.rotation.y = door.rot.y + DOOR_SWING * span(ms, ...U.swing);
    }
  }

  // screen position of a world point through the fisheye (the inverse of the grade pass's mapping)
  const view = new THREE.Vector3();
  function project(p, out) {
    view.copy(p).applyMatrix4(camera.matrixWorldInverse);
    const theta = Math.acos(THREE.MathUtils.clamp(-view.z / view.length(), -1, 1));
    const phi = Math.atan2(view.y, view.x);
    const r = THREE.MathUtils.lerp(
      lens.focal * Math.tan(Math.min(theta, 1.5)),
      2 * lens.focal * Math.sin(theta / 2),
      lens.strength,
    );
    const w = container.clientWidth;
    const h = container.clientHeight;
    out.x = (0.5 + (r * Math.cos(phi)) / 2) * w;
    out.y = (0.5 - (r * Math.sin(phi) * (w / h)) / 2) * h;
  }
  const anchorPx = { x: 0, y: 0 };
  const formPx = { x: 0, y: 0 };
  const insidePos = new THREE.Vector3();

  // Adaptive resolution: steady slow frames step the internal resolution down (and back up).
  let lastNow = 0;
  let frameAcc = 0;
  let frameCount = 0;
  const adapt = (now) => {
    const dt = lastNow ? now - lastNow : 0;
    lastNow = now;
    if (dt <= 0 || dt > 60) return; // hitches (tab switch, GC) don't count
    frameAcc += dt;
    frameCount++;
    if (frameCount < 90) return;
    const avg = frameAcc / frameCount;
    frameAcc = 0;
    frameCount = 0;
    const next = THREE.MathUtils.clamp(avg > 24 ? quality - 0.1 : avg < 15 ? quality + 0.1 : quality, 0.7, 1);
    if (Math.abs(next - quality) > 0.01) {
      quality = next;
      resize();
    }
  };

  const tick = (now) => {
    raf = requestAnimationFrame(tick);
    if (!ready) return;
    // the window moved to a screen with another pixel density (no resize event for that)
    if (window.devicePixelRatio !== dpr) resize();
    const s = now / 1000;
    const elapsed = startAt < 0 ? -1 : now - startAt;
    if (startAt >= 0) adapt(now);

    // intro: the neon strikes, the room comes up behind it, the camera settles in
    const fast = opts.reducedMotion;
    const u = elapsed < 0 ? 0 : fast ? 1 : (elapsed - STRIKE_AT) / STRIKE_MS;
    const tubes = strike(u);
    const room = elapsed < 0 ? 0 : fast ? 1 : smooth(0.25, 1.4, u);

    // the hold charges the door: the mark brightens and the neon swells a little
    charge += (chargeTarget - charge) * 0.18;
    const um = unlockAt < 0 ? -1 : fast ? 99999 : now - unlockAt;
    const flash = um < 0 ? 0 : Math.sin(Math.PI * span(um, ...U.flash, (x) => x)); // one bright pulse
    const markBoost = 1 + 2.2 * charge + 3.5 * flash;
    const swell = 1 + 0.35 * charge * (0.85 + 0.15 * Math.sin(s * 18.0));
    applyPower(room, tubes * swell * (0.97 + 0.03 * Math.sin(s * 11.0) * Math.sin(s * 7.3)), markBoost);
    if (treasure) {
      treasure.group.visible = um >= 0;
      if (um >= 0) {
        // the room's light comes up as the door swings clear of the opening
        treasure.setLevel(fast ? 1 : span(um, U.swing[0] - 100, U.swing[1] - 300));
        treasure.update(s);
      }
    }
    if (um >= 0) {
      animateUnlock(um);
      if (!opened && um >= U.opened) {
        opened = true;
        cb.onOpened?.();
      }
    }
    const k = elapsed < 0 ? 0 : fast ? 1 : easeOutCubic(Math.min(1, elapsed / INTRO_MS));

    // settle: from a touch further back and lower, to the rest pose; then a slow drift with the pointer
    pointerSmooth.lerp(pointer, 0.04);
    const live = fast ? 0 : smooth(0.6, 1, k);
    camera.position.copy(rest.pos);
    camera.position.z += (1 - k) * 0.9;
    camera.position.y -= (1 - k) * 0.12;
    camera.position.x += (Math.sin(s * 0.25) * 0.05 + pointerSmooth.x * 0.12) * live;
    camera.position.y += (Math.sin(s * 0.37) * 0.02 - pointerSmooth.y * 0.05) * live;
    camera.rotation.set(rest.pitch + pointerSmooth.y * 0.006 * live, -pointerSmooth.x * 0.012 * live, 0);
    // holding leans you in a little; the bolts letting go are felt as a jolt; then, as the door swings,
    // the camera moves in on the opening until the dark fills most of the frame
    camera.position.z -= Math.max(charge, um < 0 ? 0 : 1) * 0.18;
    const inside = um < 0 ? 0 : span(um, U.swing[0] + 200, U.swing[1] + 1500);
    if (inside > 0 && manifest) {
      const axisY = manifest.doorAxis[2]; // Blender Z = height
      camera.position.lerp(insidePos.set(camera.position.x * 0.3, axisY - INSIDE_RISE, INSIDE_DIST), inside);
      camera.rotation.x = THREE.MathUtils.lerp(camera.rotation.x, Math.atan2(INSIDE_RISE, INSIDE_DIST), inside);
    }
    if (um > U.clunk && um < U.clunk + 260 && !fast) {
      const j = 1 - (um - U.clunk) / 260;
      camera.position.y += Math.sin(um * 0.09) * 0.012 * j;
    }

    if (opts.anchorEl && hasAnchor) {
      camera.updateMatrixWorld();
      project(anchorPoint, anchorPx);
      // on narrower windows the wall point nears the edge: keep the whole control on screen
      const half = opts.anchorEl.offsetWidth / 2 + 24;
      const x = THREE.MathUtils.clamp(anchorPx.x, half, Math.max(half, container.clientWidth - half));
      opts.anchorEl.style.transform = `translate(${x.toFixed(1)}px, ${anchorPx.y.toFixed(1)}px) translate(-50%, -50%)`;
    }
    if (opts.formEl && treasure?.markBottom && um >= 0) {
      camera.updateMatrixWorld();
      project(treasure.markBottom, formPx);
      // a breath under the mark, but never so low that the form runs off the bottom
      const h = container.clientHeight;
      const top = Math.min(formPx.y + h * 0.06, h - opts.formEl.offsetHeight - 24);
      opts.formEl.style.setProperty('--aops-open-top', `${Math.max(top, h * 0.3).toFixed(1)}px`);
    }

    post.render(s);

    if (!introDone && elapsed >= 0 && (fast || elapsed > INTRO_MS * 0.6)) {
      introDone = true;
      cb.onIntroDone();
    }
  };
  raf = requestAnimationFrame(tick);

  const onVisibility = () => {
    cancelAnimationFrame(raf);
    lastNow = 0;
    if (!document.hidden && !disposed) raf = requestAnimationFrame(tick);
  };
  document.addEventListener('visibilitychange', onVisibility);

  load().catch((err) => {
    if (!disposed) cb.onError(err);
  });

  return {
    start() {
      if (startAt < 0) startAt = performance.now();
      // the hold-to-unlock is live from the first frame: fetch the room behind the door right away
      prefetchTreasure();
    },
    setCharge(p) {
      chargeTarget = THREE.MathUtils.clamp(p, 0, 1);
    },
    unlock() {
      if (unlockAt < 0) unlockAt = performance.now();
    },
    dispose() {
      disposed = true;
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      window.removeEventListener('pointermove', onPointer);
      document.removeEventListener('visibilitychange', onVisibility);
      disposables.forEach((d) => d.dispose());
      renderer.dispose();
      renderer.domElement.remove();
    },
  };
}
